import { api } from './client';
import type {
  BarcodeMapping,
  BarcodeResolveResult,
  LabelRecord,
  MonthlyProductSales,
  PrinterInfo,
  ProductSalesSummary,
  QueueItem,
  VinmonopoletProduct,
  WineLabelReview,
  WineAsset,
  WineExtraction,
  WineRating,
  WineRatingsSummary,
  WineScan,
  ProductCandidate,
  WineLabelPanePayload,
} from './types';

// ─── Catalog mirror ───────────────────────────────────────────────────
export interface CatalogEnrichment {
  catalogId: string;
  name: string;
  producer?: string;
  country?: string;
  region?: string;
  type?: string;
  body?: string;
  acidity?: string;
  abv?: number;
  grapes: string[];
  foodPairings: string[];
  vintages: (number | 'N.V.')[];
  website?: string;
}

export const Catalog = {
  search(q: string, limit = 10) {
    return api<{ products: VinmonopoletProduct[] }>('/api/catalog/products/search', { query: { q, limit } });
  },
  product(productId: string) {
    return api<{ product: VinmonopoletProduct }>(`/api/catalog/products/${productId}`);
  },
  monthlySales(productId: string, from?: string, to?: string) {
    return api<{ sales: ProductSalesSummary | { months: MonthlyProductSales[] } }>(
      `/api/catalog/products/${productId}/monthly-sales`,
      { query: { from, to } },
    );
  },
  enrichment(input: { name: string; producer?: string; country?: string; region?: string }) {
    return api<{ ok: true; data: { enrichment: CatalogEnrichment } }>(
      '/api/v1/catalog/enrichment',
      { query: input },
    ).then((r) => r.data.enrichment);
  },
};

// ─── Barcodes ─────────────────────────────────────────────────────────
export const Barcodes = {
  resolve(barcode: string, opts?: { includeStats?: boolean; candidateLimit?: number; from?: string; to?: string }) {
    return api<BarcodeResolveResult>(`/api/barcodes/${encodeURIComponent(barcode)}/resolve`, {
      query: {
        includeStats: opts?.includeStats,
        candidateLimit: opts?.candidateLimit,
        from: opts?.from,
        to: opts?.to,
      },
    });
  },
  confirm(barcode: string, payload: { productId: string; source?: string; note?: string }) {
    return api<{ mapping: BarcodeMapping; product: VinmonopoletProduct }>(
      `/api/barcodes/${encodeURIComponent(barcode)}/confirm`,
      { method: 'POST', body: payload },
    );
  },
  mappings() {
    return api<{ mappings: BarcodeMapping[] }>(`/api/barcodes/mappings`);
  },
};

// ─── Wines (AI scan + ratings + assets) ───────────────────────────────
export interface ScanResponse {
  scan: WineScan;
  extraction: WineExtraction;
  candidates: ProductCandidate[];
}

export const Wines = {
  scanLabel(payload: { imageBase64?: string; imageUrl?: string; mimeType?: string; candidateLimit?: number }) {
    return api<ScanResponse>('/api/wines/scan-label', { method: 'POST', body: payload });
  },
  getRatings(productId: string) {
    return api<{ ratings: WineRating[] | WineRatingsSummary }>(`/api/wines/${encodeURIComponent(productId)}/ratings`)
      .then((response) => {
        if (Array.isArray(response.ratings)) {
          return {
            ratings: response.ratings,
            summary: {
              productId,
              average: response.ratings.length
                ? response.ratings.reduce((sum, item) => sum + item.rating, 0) / response.ratings.length
                : null,
              count: response.ratings.length,
              ratings: response.ratings,
            } satisfies WineRatingsSummary,
          };
        }

        return {
          ratings: response.ratings.ratings,
          summary: response.ratings,
        };
      });
  },
  upsertMyRating(productId: string, payload: { rating: number; notes?: string }) {
    return api<{ rating: WineRating }>(`/api/wines/${encodeURIComponent(productId)}/ratings/me`, {
      method: 'PUT',
      body: payload,
    });
  },
  getAssets(productId: string) {
    return api<{ assets: WineAsset[] }>(`/api/wines/${encodeURIComponent(productId)}/assets`);
  },
  addAsset(productId: string, payload: Omit<WineAsset, 'id' | 'productId' | 'createdAt'>) {
    return api<{ asset: WineAsset }>(`/api/wines/${encodeURIComponent(productId)}/assets`, {
      method: 'POST',
      body: payload,
    });
  },
  searchAssets(q: string, limit = 10) {
    return api<{ assets: WineAsset[] }>(`/api/wines/assets/search`, { query: { q, limit } });
  },
  searchVivino(q: string, limit = 10) {
    return api<{ results: unknown[] }>(`/api/wines/enrichment/vivino/search`, { query: { q, limit } });
  },
};

// ─── Labels / Print ───────────────────────────────────────────────────
export interface CreateLabelPayload {
  labels?: WineLabelPanePayload[];
  template?: string;
  quantity?: number;
  duplicateAcrossRow?: boolean;
  debugFrame?: boolean;
  printerName?: string;
  deliveryTarget?: unknown;
  printImmediately?: boolean;
}

export interface PrintBottleLabelsPayload {
  bottleIds: string[];
  layout?: string;
  includeBarcode?: boolean;
  includeQr?: boolean;
  async?: boolean;
  debugFrame?: boolean;
  printerName?: string;
  deliveryTarget?: unknown;
}

export const Labels = {
  list() { return api<{ labels: LabelRecord[] }>('/api/labels'); },
  get(id: string) { return api<{ label: LabelRecord }>(`/api/labels/${id}`); },
  zpl(id: string) { return api<{ zpl: string }>(`/api/labels/${id}/zpl`); },
  preview(payload: CreateLabelPayload & WineLabelPanePayload) {
    return api<{ rendered: { zpl: string; warnings?: { message: string }[]; review?: WineLabelReview } }>('/api/labels/preview-zpl', {
      method: 'POST',
      body: payload,
    });
  },
  create(payload: CreateLabelPayload & WineLabelPanePayload) {
    return api<{ label: LabelRecord }>('/api/labels', { method: 'POST', body: payload });
  },
  printBottles(payload: PrintBottleLabelsPayload) {
    return api<{ ok: true; data: { jobs?: unknown[]; job?: unknown } }>('/api/v1/labels/print', {
      method: 'POST',
      body: payload,
    }).then((r) => r.data);
  },
  print(id: string, payload: { printerName?: string; deliveryTarget?: unknown }) {
    return api<{ label: LabelRecord; queueJob: QueueItem; printResult?: unknown }>(
      `/api/labels/${id}/print`,
      { method: 'POST', body: payload },
    );
  },
  destroy(id: string) {
    return api<void>(`/api/labels/${id}`, { method: 'DELETE' });
  },
  queue() { return api<{ queue: QueueItem[] }>('/api/labels/queue'); },
  pause(id: string) { return api<{ queueJob: QueueItem }>(`/api/labels/queue/${id}/pause`, { method: 'POST' }); },
  resume(id: string) {
    return api<{ label: LabelRecord; queueJob: QueueItem; printResult?: unknown }>(
      `/api/labels/queue/${id}/resume`,
      { method: 'POST' },
    );
  },
  clearCompleted() {
    return api<{ queue: QueueItem[] }>('/api/labels/queue/actions/clear-completed', { method: 'POST' });
  },
};

// ─── Print service ────────────────────────────────────────────────────
export const Print = {
  status() {
    return api<{
      service: string;
      localPrinter: { mode: string; ok?: boolean; printerName?: string; defaultPrinterName?: string | null; supportsLocalPrinting?: boolean };
      queue?: { queued?: number; printing?: number; failed?: number };
    }>('/api/print');
  },
  printers() { return api<{ printers: PrinterInfo[] }>('/api/print/printers'); },
};

// ─── Receipt OCR ──────────────────────────────────────────────────────
export const Receipts = {
  ocr(input: { imageBase64: string; mimeType?: string }) {
    return api<{ ok: true; data: { text: string; confidence: number; durationMs: number } }>(
      '/api/v1/receipts/ocr',
      { method: 'POST', body: input },
    ).then((r) => r.data);
  },
};

// ─── Health ───────────────────────────────────────────────────────────
export const System = {
  health() {
    return api<{
      ok: boolean;
      providers: { name: string; enabled: boolean; reason?: string; auth?: string; baseUrl?: string }[];
      enrichment: { vivino: { enabled: boolean } };
      ai?: {
        openai?: { enabled: boolean; visionModel?: string; textModel?: string };
        recommendations?: { enabled: boolean; mode?: string; model?: string };
      };
      printing: { mode: string; ok?: boolean; defaultPrinterName?: string | null; supportsLocalPrinting?: boolean };
    }>('/health');
  },
};
